import {
  Component,
  inject,
  input,
  output,
  OnInit,
  DestroyRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroupDirective,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { FormFieldComponent } from 'src/app/shared/form-field/components/form-field/form-field.component';
import { FormFieldsDirective } from 'src/app/shared/form-field/directives/form-fields/form-fields.directive';
import { FormFieldService } from 'src/app/shared/form-field/services/form-field/form-field.service';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { MatButtonModule } from '@angular/material/button';
import { GenerateKeyRequestDto } from 'src/app/shared/services/api/dto/generate-key-request-dto';
import { CryptoToken } from 'src/app/shared/entities/crypto-token';
import { ApiService } from 'src/app/shared/services/api/api.service';
import { catchError, finalize, of, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import {
  CreateCryptoTokenKeyField,
  cryptoTokenKeyFormValueToDto,
} from './create-crypto-token-key.types';
import { CryptotokenKeyDataService } from 'src/app/shared/store/features/cryptotoken-keys/cryptotoken-keys.service';
import { ValidationConstants } from 'src/app/shared/validators/validation-constants';
import { FormFieldOptions } from 'src/app/shared/form-field/form-field.types';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';

@Component({
  selector: 'app-create-crypto-token-key',
  imports: [
    SpinnerComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    FormFieldsDirective,
    MatButtonModule,
  ],
  providers: [FormFieldService],
  templateUrl: './create-crypto-token-key.component.html',
  styleUrl: './create-crypto-token-key.component.scss',
})
export class CreateCryptoTokenKeyComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private langService = inject(LangService);
  private dialogService = inject(DialogService);
  private formFieldService = inject(FormFieldService);
  private apiService = inject(ApiService);
  private toastrService = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private cryptoTokenKeyDataService = inject(CryptotokenKeyDataService);
  labels =
    this.langService.lang.features.cryptotokenlist.details.cryptoTokenKeys;

  @ViewChild(FormGroupDirective)
  private formRef!: FormGroupDirective;

  value = input<CryptoToken>();

  isCreating = false;

  createdKey = output<GenerateKeyRequestDto>();
  cancelCreation = output();

  form = this.fb.group({
    [CreateCryptoTokenKeyField.alias]: [
      '',
      [
        Validators.required,
        Validators.pattern(ValidationConstants.cryptotoken.namePattern),
      ],
    ],
    [CreateCryptoTokenKeyField.keySpecification]: ['', [Validators.required]],
  });

  CreateCryptoTokenKeyField = CreateCryptoTokenKeyField;

  onCancel() {
    this.dialogService.withConfirmation({
      text: this.labels.actions.create.confirmCancel,
      confirmIf: () => this.form.dirty,
    })(() => {
      this.cancelCreation.emit(undefined);
    });
  }

  onSubmit() {
    if (this.form.valid) {
      this.form.markAsDirty();
      const dto = cryptoTokenKeyFormValueToDto(this.form.value);
      this.createdKey.emit(dto);
      this.onRequest(dto);
      this.form.reset();
      this.formRef.resetForm();
    }
  }

  private onSuccess() {
    this.toastrService.success(this.labels.toasts.created);
    const tokenName = this.value()?.tokenName;
    if (tokenName) {
      this.cryptoTokenKeyDataService.ensure(tokenName);
    }
  }

  private onFail(error: unknown) {
    console.error('Key creation error', error);
    const defaultToast = this.labels.toasts.fail;
    const beToast = extractErrorMessage(error);
    this.toastrService.error(beToast || defaultToast);
  }

  onRequest(dto: GenerateKeyRequestDto) {
    this.isCreating = true;
    this.apiService
      .createCryptotokenKey(this.value()?.tokenName ?? '', dto)
      .pipe(
        tap(() => {
          this.onSuccess();
        }),
        catchError(error => {
          this.onFail(error);
          return of();
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isCreating = false;
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.formFieldService.configure({
      labels: this.labels.actions.create.inputs,
      options: {
        [CreateCryptoTokenKeyField.alias]: FormFieldOptions.text({
          required: true,
        }),
        [CreateCryptoTokenKeyField.keySpecification]: FormFieldOptions.select({
          required: true,
        }),
      },
    });
  }

  ngOnDestroy() {
    this.cryptoTokenKeyDataService.clear();
  }
}
